<template>
  <div class="table-list-wrapper" :class="hidenAdaptWrapper ? '' : 'adapt-wrapper'">
    <div class="table-header" v-if="!hidenHeader">
      <div class="filter-group">
        <slot name="filter-group"></slot>
      </div>
      <div class="btn-group">
        <slot name="btn-group">
          <el-button v-if="showCreateBtn" class="create-btn" @click="handleCreate">{{ createBtnLabelWatcher }}</el-button>
        </slot>
      </div>
    </div>
    <div class="table-container">
      <el-table
        v-loading="loading"
        border
        ref="elTable"
        :height="tableHeight"
        :data="listData"
        :row-key="rowKey"
        :row-class-name="rowClassName"
        :default-expand-all="defaultExpandAll"
        :empty-text="$t('el.table.emptyText')"
        :tree-props="treeProps"
        :span-method="spanMethod"
        @row-click="handleRowClick"
        @selection-change="handleSelectionChange"
        @select-all="handleSelectAll"
      >
        <slot>
          <el-table-column
            v-for="(item, index) in tableColumns"
            :key="index"
            :type="item.columnType"
            :class-name="item.cellClass"
            :label="item.label"
            :prop="item.dataKey"
            :resizable="!item.disableResize"
            :min-width="item.width"
            :sortable="item.sort && item.sort.enable"
            :sort-method="item.sort && item.sort.handler"
            :sort-by="item.sort && item.sort.by"
            :sort-orders="item.sort && item.sort.orders"
            :filters="item.filter && item.filter.options"
            :filter-method="item.filter && item.filter.handler"
            :show-overflow-tooltip="item.hasOwnProperty('showOverflowTooltip') ? item.showOverflowTooltip : true"
          >
            <template v-if="item.headerSlotName" #header="{ column, $index }">
              <slot v-bind="{ column, $index }" :name="item.headerSlotName"></slot>
            </template>
            <template v-if="item.dataKey" #default="{ row, column, $index }">
              <slot v-if="item.slotName === 'dragSort' && enableDragSort">
                <i class="el-icon-sort draggable-handle"></i>
              </slot>
              <slot
                v-else-if="item.slotName"
                v-bind="{ column, $index }"
                :name="item.slotName"
                :row="row"
                :data="getColumnData(row, item)"
                :formattedData="getFormattedColumnData(row, item)"
              >
                {{ getFormattedColumnData(row, item) }}
              </slot>
              <template v-else>{{ getFormattedColumnData(row, item) }}</template>
            </template>
          </el-table-column>
        </slot>
        <el-table-column v-if="!noOperation" class-name="operate-btn-group" :label="$t('common.label.operate')" :resizable="!disableOperateResize">
          <template #default="{ row }">
            <slot name="operate-btn-group" :row="row">
              <el-button
                v-if="getRowBtnIsShow(rowBtns.edit, row)"
                type="text"
                :disabled="getRowBtnDisabled(rowBtns.edit, row)"
                @click="handleEdit(row)">
                {{ getRowBtnLabel(rowBtns.edit, row) }}
              </el-button>
              <el-button
                v-if="getRowBtnIsShow(rowBtns.enable, row)"
                type="text"
                :disabled="getRowBtnDisabled(rowBtns.enable, row)"
                @click="handleEnable(row)">
                {{ getRowBtnLabel(rowBtns.enable, row) }}
              </el-button>
              <el-button
                v-if="getRowBtnIsShow(rowBtns.delete, row)"
                type="text"
                :disabled="getRowBtnDisabled(rowBtns.delete, row)"
                @click="handleDelete(row)">
                {{ getRowBtnLabel(rowBtns.delete, row) }}
              </el-button>
            </slot>
            <slot name="operate-btn-extra-group" :row="row"></slot>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import Sortable from 'sortablejs'

export default {
  props: {
    showCreateBtn: {
      type: Boolean,
      default: true
    },
    defaultExpandAll: {
      type: Boolean,
      default: true
    },
    createBtnLabel: {
      type: String,
      default: ''
    },
    rowKey: {
      default: 'id'
    },
    rowClassName: {
      default: null
    },
    listData: {
      type: Array,
      required: true
    },
    tableHeight: {
      default: null
    },
    tableColumns: {
      type: Array,
      default () {
        return []
      }
    },
    tableRowBtns: {
      type: Object,
      default () {
        return {}
      }
    },
    disableOperateResize: {
      type: Boolean
    },
    noOperation: {
      type: Boolean,
      default: false
    },
    spanMethod: {
      required: false
    },
    treeProps: {
      type: Object,
      required: false,
      default () { return { children: 'children', hasChildren: 'hasChildren' } }
    },
    loading: {
      type: Boolean,
      default: false
    },
    dragSort: {
      type: Object,
      default () {
        return {
          enable: false,
          dataKey: 'sort',
          after: () => {}
        }
      }
    },
    // adapt-wrapper class 存在会导致Table 头灰色,整个Table 字体大小 12px
    hidenAdaptWrapper: {
      type: Boolean,
      default: false
    },
    hidenHeader: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {

    }
  },
  computed: {
    elTable () {
      return this.$refs.elTable
    },
    enableDragSort () {
      return this.dragSort?.enable
    },
    createBtnLabelWatcher () {
      return this.createBtnLabel || this.$t('common.btn.add')
    },
    rowBtns () {
      const rowBtns = {
        edit: {
          isShow: true,
          label: this.$t('common.btn.edit')
        },
        enable: {
          isShow: false,
          label: (row) => {
            if (row.status) {
              return this.$t('common.btn.disable')
            } else {
              return this.$t('common.btn.enable')
            }
          }
        },
        delete: {
          isShow: false,
          label: this.$t('common.btn.delete')
        }
      }
      _.merge(rowBtns, this.tableRowBtns)
      return rowBtns
    }
  },
  created () {
    if (this.enableDragSort && !this.tableColumns.find((item) => item.slotName === 'dragSort')) {
      const sortColumn = {
        label: this.$t('common.label.sortOperate'),
        dataKey: this.dragSort.dataKey || 'sort',
        slotName: 'dragSort',
        width: '50px'
      }
      const newTableColumns = [...this.tableColumns, sortColumn]
      this.$emit('update:tableColumns', newTableColumns)
    }
  },
  mounted () {
    if (this.enableDragSort) {
      const table = document.querySelector('.el-table__body-wrapper tbody')
      Sortable.create(table, {
        handle: '.draggable-handle',
        onEnd: ({ newIndex, oldIndex }) => {
          const originSortArray = this.listData.map((item) => item[this.dragSort.dataKey])
          const targetRow = this.listData.splice(oldIndex, 1)[0]
          this.listData.splice(newIndex, 0, targetRow)
          this.listData.forEach((element, index) => {
            element[this.dragSort.dataKey] = originSortArray[index]
          })
          if (Object.prototype.toString.call(this.dragSort.after) === '[object Function]') {
            this.dragSort.after()
          }
        }
      })
    }
  },
  methods: {
    handleRowClick (row, column, event) {
      this.$emit('row-click', row, column, event)
    },
    getColumnData (rowData, columnConfig) {
      return _.get(rowData, columnConfig.dataKey)
    },
    getFormattedColumnData (rowData, columnConfig) {
      let columnData = this.getColumnData(rowData, columnConfig)
      if (columnConfig.formatter) {
        columnData = columnConfig.formatter(columnData, rowData)
      }
      return columnData
    },
    getRowBtnIsShow (btnConfig, row) {
      if (typeof btnConfig.isShow === 'function') {
        return btnConfig.isShow(row)
      } else {
        return btnConfig.isShow
      }
    },
    getRowBtnLabel (btnConfig, row) {
      if (typeof btnConfig.label === 'function') {
        return btnConfig.label(row)
      } else {
        return btnConfig.label
      }
    },
    getRowBtnDisabled (btnConfig, row) {
      if (typeof btnConfig.disabled === 'function') {
        return btnConfig.disabled(row)
      } else {
        return btnConfig.disabled
      }
    },
    handleCreate () {
      this.$emit('create')
    },
    handleEdit (rowData) {
      this.$emit('edit', rowData)
    },
    handleEnable (rowData) {
      this.$emit('enable', rowData)
    },
    handleDelete (rowData) {
      this.$emit('delete', rowData)
    },
    handleSelectionChange (selection) {
      this.$emit('selection-change', selection)
    },
    handleSelectAll (selection) {
      this.$emit('select-all', selection)
    }
  }
}
</script>

<style lang="scss" scoped>
$headerHeight: 30px;

.draggable-handle {
  font-size: 16px;
  cursor: grab;
}

.table-list-wrapper {
  .table-header {
    display: flex;
    margin-top: 10px;
    margin-bottom: 10px;
    height: $headerHeight;
    line-height: $headerHeight;

    .filter-group {
      display: flex;
      margin-right: 10px;
      flex: 1;

      ::v-deep {
        > * {
          margin-right: 10px;
          vertical-align: middle;

          &:last-child {
            margin-right: 0;
          }
        }

        .el-input__inner {
          height: $headerHeight;
          line-height: $headerHeight;
        }

        .el-input__icon {
          line-height: $headerHeight;
        }
      }
    }

    .btn-group {
      margin-left: auto;
      margin-right: 0;

      ::v-deep {
        .create-btn {
          padding: 9px 20px;
          border: none;
          background-color: #ff6633;
          color: #fff;
          font-weight: bold;

          &:hover {
            background-color: #ff8855;
          }
        }
      }
    }
  }

  .table-container {
    .el-table {
      .operate-btn-group {
        .el-button {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
    }
  }
}
</style>
